import { injectable, inject } from 'inversify';
import { Action } from '@openfin/predux';

import { Inject } from '../common/Injectables';
import {
  CreateNotification,
  RemoveNotifications,
  RegisterApplication,
  ToggleCenterLocked,
  ToggleCenterMuted,
  UpdateAppMuteState,
  UpdateAppToastPersistState,
  RegisterStream,
  UpdateStreamMuteState,
  UpdateNotification,
} from '../store/Actions';
import { ServiceStore } from '../store/ServiceStore';
import { Database, CollectionMap } from '../model/database/Database';
import { DatabaseError } from '../model/Errors';
import { RootState } from '../store/State';
import { SettingsMap } from '../model/StoredSetting';
import { SetCenterPosition, SetToastDuration, SetToastPosition } from '../store/partials/Settings';
import { ApplyNotificationModifier } from '../store/partials/Notification';

@injectable()
export class Persistor {
  private readonly _store: ServiceStore;
  private readonly _database: Database;

  constructor(
    @inject(Inject.STORE) store: ServiceStore,
    @inject(Inject.DATABASE) database: Database
  ) {
    this._store = store;
    this._database = database;
    this._store.onAction.add(this.onAction, this);
  }

  private async onAction(action: Action<RootState>): Promise<void> {
    if (action instanceof CreateNotification) {
      const { notification } = action;

      if (notification.source.type === 'desktop') {
        try {
          await this._database.get(CollectionMap.NOTIFICATIONS).upsert(notification);
        } catch (error) {
          throw new DatabaseError(`Unable to upsert notification ${notification.id}`, error);
        }
      }
    } else if (action instanceof UpdateNotification) {
      const { updatedNotification } = action;
      if (!updatedNotification) {
        console.warn(`Updated Notification was undefined for id: ${action.updatePayload.id}`);
        return;
      }

      if (updatedNotification.source.type === 'desktop') {
        try {
          await this._database.get(CollectionMap.NOTIFICATIONS).upsert(updatedNotification);
        } catch (error) {
          throw new DatabaseError(`Unable to upsert notification ${updatedNotification.id}`, error);
        }
      }
    } else if (action instanceof RemoveNotifications) {
      const { notifications } = action;
      const ids = notifications
        .filter((note) => note.source.type === 'desktop')
        .map((note) => note.id);

      if (ids.length > 0) {
        try {
          await this._database.get(CollectionMap.NOTIFICATIONS).delete(ids);
        } catch (error) {
          throw new DatabaseError(`Unable to delete notification ${ids}`, error);
        }
      }
    } else if (action instanceof ApplyNotificationModifier) {
      const { notification } = action;

      try {
        await this._database.get(CollectionMap.NOTIFICATIONS).upsert(notification);
      } catch (error) {
        throw new DatabaseError(
          `Unable to apply modifier to notificiation ${notification.id}`,
          error
        );
      }
    } else if (action instanceof RegisterApplication) {
      const { application } = action;

      try {
        const collection = this._database.get(CollectionMap.APPLICATIONS);
        const existingApp = await collection.get(application.id);

        // Preserve mute and toast persistence states
        await collection.upsert({
          ...application,
          muted: existingApp ? existingApp.muted : application.muted,
          persistToasts: existingApp ? existingApp.persistToasts : application.persistToasts,
        });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert Client info ${application.id}`, error);
      }
    } else if (action instanceof RegisterStream) {
      const { stream } = action;

      try {
        const collection = this._database.get(CollectionMap.STREAMS);
        const existingStream = await collection.get(stream.id);

        // Add a legacy app ID to streams created before 1.9.0
        if (existingStream && !existingStream.appId) {
          stream.appId = 'legacy-stream';
        }

        // Preserve mute persistence state
        await collection.upsert({
          ...stream,
          muted: existingStream ? existingStream.muted : false,
        });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert Stream info ${stream.id}`, error);
      }
    } else if (action instanceof UpdateAppMuteState) {
      const { application, muted } = action;

      try {
        const collection = this._database.get(CollectionMap.APPLICATIONS);

        await collection.upsert({ ...application, muted });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert Client info ${application.id}`, error);
      }
    } else if (action instanceof UpdateStreamMuteState) {
      const { stream, muted } = action;

      try {
        const collection = this._database.get(CollectionMap.STREAMS);

        await collection.upsert({ ...stream, muted });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert Stream info ${stream.id}`, error);
      }
    } else if (action instanceof UpdateAppToastPersistState) {
      const { application, persistToasts } = action;

      try {
        const collection = this._database.get(CollectionMap.APPLICATIONS);

        await collection.upsert({ ...application, persistToasts });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert Client info ${application.id}`, error);
      }
    } else if (action instanceof ToggleCenterLocked) {
      const centerLocked = this._store.state.centerLocked;

      try {
        const collection = this._database.get(CollectionMap.SETTINGS);

        await collection.upsert({ id: SettingsMap.CenterLocked, value: centerLocked });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert centerLocked setting to ${centerLocked}`, error);
      }
    } else if (action instanceof ToggleCenterMuted) {
      const centerMuted = this._store.state.centerMuted;

      try {
        const collection = this._database.get(CollectionMap.SETTINGS);

        await collection.upsert({ id: SettingsMap.CenterMuted, value: centerMuted });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert centerMuted setting to ${centerMuted}`, error);
      }
    } else if (action instanceof SetCenterPosition) {
      const { position } = action;

      try {
        const collection = this._database.get(CollectionMap.SETTINGS);

        await collection.upsert({ id: SettingsMap.CenterPosition, value: position });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert centerPosition setting to ${position}`, error);
      }
    } else if (action instanceof SetToastPosition) {
      const { position } = action;

      try {
        const collection = this._database.get(CollectionMap.SETTINGS);

        await collection.upsert({ id: SettingsMap.ToastPosition, value: position });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert toastPosition setting to ${position}`, error);
      }
    } else if (action instanceof SetToastDuration) {
      const value = this._store.state.settings.toastDuration;
      const id = SettingsMap.ToastDuration;

      try {
        const collection = this._database.get(CollectionMap.SETTINGS);

        await collection.upsert({ id, value });
      } catch (error) {
        throw new DatabaseError(`Unable to upsert ${id} setting to ${value}`, error);
      }
    }
  }
}
